import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MapPin, Search, Clock, Loader2 } from "lucide-react";
import { ScanTextInput } from "@/components/ScanTextInput";

// WHICH SECTOR — tapped, not remembered and typed.
//
// His yard sectors have always existed as locations; what didn't exist was any way to choose one
// without knowing its code by heart. So they are buttons, and the order is by RECENCY (owner
// ruling 2026-09-25: "a sector I filled last year stays at the top forever" if it goes by total
// count). Two deliberate details:
//   · THE ORDER IS FROZEN WHEN THIS OPENS. Placing thirty trucks into the back row must not
//     reshuffle the button out from under his thumb halfway through — "reordering mid-job is worse
//     than a fixed list".
//   · TYPING AND SCANNING STILL WORK, for the tablet in the yard and for anywhere not on the list
//     (a warehouse position, a new spot). The list is the fast path, not the only path.
export function SectorPicker({ onPick, picked, testid = "sector", big = false }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [typed, setTyped] = useState("");
  const frozen = useRef(null);

  useEffect(() => {
    api.get("/donors/place-sectors")
      .then(({ data }) => { frozen.current = data.items || []; setRows(frozen.current); })
      .catch(() => setRows([]));
  }, []);

  const shown = useMemo(() => {
    const list = rows || [];
    if (!q.trim()) return list;
    const n = q.trim().toLowerCase();
    return list.filter((s) => `${s.code} ${s.name}`.toLowerCase().includes(n));
  }, [rows, q]);

  if (rows === null) {
    return <p className="text-xs text-slate-400 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />loading your sectors…</p>;
  }

  return (
    <div className="space-y-2" data-testid={`${testid}-picker`}>
      {rows.length === 0 ? (
        <p data-testid={`${testid}-none`} className="text-xs text-[#B01E28]">
          You have no yard sectors set up yet — nothing to pick from. Add them under
          <b> Fixed Locations</b> first, or type a code below if one exists.
        </p>
      ) : (
        <>
          {rows.length > 12 && (
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={`Find one of your ${rows.length} sectors…`}
                className="h-9 pl-8 rounded-lg text-xs" />
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto">
            {shown.map((s) => {
              const on = picked && (picked.id === s.id || picked.code === s.code);
              return (
                <button key={s.id} type="button" data-testid={`${testid}-opt-${s.code}`}
                  onClick={() => onPick(s)}
                  className={`text-left rounded-lg border-2 px-2.5 transition-colors ${big ? "min-h-14 py-2" : "min-h-11 py-1.5"} ${on ? "border-[#E0781A] bg-orange-50" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                  <span className="flex items-center gap-1.5 font-mono text-[11px] font-bold text-slate-800">
                    <MapPin className={`w-3 h-3 ${on ? "text-[#E0781A]" : "text-slate-400"}`} />{s.code}
                    {s.last_used && <Clock className="w-3 h-3 text-[#E0781A]" title="you used this recently" />}
                  </span>
                  <span className="block text-[11px] text-slate-600 max-w-[16rem] truncate">{s.name}</span>
                  <span className="block text-[10px] text-slate-400">
                    {s.trucks} truck{s.trucks === 1 ? "" : "s"} there
                    {s.holding ? <span className="text-[#C76714]"> · holding area</span> : null}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
      <div className="flex items-center gap-1.5 pt-1">
        <ScanTextInput testid={`${testid}-code-input`} value={typed} onChange={setTyped}
          onEnter={() => { if (typed.trim()) onPick({ code: typed.trim().toUpperCase(), name: typed.trim().toUpperCase(), typed: true }); }}
          placeholder="…or scan / type any location code" className="flex-1"
          inputClassName={`uppercase ${big ? "h-12 text-base" : "h-9 text-xs"}`} />
        <button type="button" data-testid={`${testid}-code-use`} disabled={!typed.trim()}
          onClick={() => onPick({ code: typed.trim().toUpperCase(), name: typed.trim().toUpperCase(), typed: true })}
          className={`rounded-lg border-2 border-slate-300 px-3 text-xs font-bold text-slate-700 disabled:opacity-40 ${big ? "h-12" : "h-9"}`}>
          Use
        </button>
      </div>
    </div>
  );
}
